import { Input } from '@creditornot/cb-input';
import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { FormRowRenderer } from '../FormRowRenderer';
import { ColumnContainer, StyledFormValueContainer, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { FormValues } from '../types';

const ColumnContainerWithGap = styled(ColumnContainer)`
  gap: 24px;
`;

export const ExternalHyperlink = ({ sidePanelMode, offer, menuLocalizer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const menuFeatures = useOfferingMenuFeaturesContext();
  const intl = useIntl();
  const { values } = useFormState<FormValues>();

  const shouldHideRow = useMemo(() => {
    const shouldShowForOtcHyperlink =
      menuFeatures.overTheCounter && menuFeatures.overTheCounterHyperlinkAllowed && values.isOTCRestricted;

    const shouldShowForExternalInfoHyperlink = menuFeatures.externalInfoHyperlinkAllowed;

    if (sidePanelMode === 'create') {
      return !shouldShowForOtcHyperlink && !shouldShowForExternalInfoHyperlink;
    }

    return !shouldShowForOtcHyperlink && !shouldShowForExternalInfoHyperlink && !offer?.external_info_hyperlink;
  }, [
    menuFeatures.externalInfoHyperlinkAllowed,
    menuFeatures.overTheCounter,
    menuFeatures.overTheCounterHyperlinkAllowed,
    offer?.external_info_hyperlink,
    sidePanelMode,
    values.isOTCRestricted,
  ]);

  const previewComponent = (
    <div>
      <StyledFormValueContainer label={intl.formatMessage({ id: 'menu-item.external-item-link.hyperlink-label' })}>
        {offer?.external_info_hyperlink?.hyperlink || '-'}
      </StyledFormValueContainer>
      <StyledFormValueContainer label={intl.formatMessage({ id: 'menu-item.external-item-link.text-label' })}>
        {menuLocalizer(offer?.external_info_hyperlink?.text ?? [], true) || '-'}
      </StyledFormValueContainer>
    </div>
  );

  const editComponent = (
    <ColumnContainerWithGap>
      <Field<string> name="externalInfoHyperlink.hyperlink">
        {field => (
          <RFFFieldErrorWrapper field={field}>
            {input => (
              <StyledInputContainer label={intl.formatMessage({ id: 'menu-item.external-item-link.hyperlink-label' })}>
                <Input
                  {...input}
                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                  disabled={!menuPermissions.contentEdit}
                />
              </StyledInputContainer>
            )}
          </RFFFieldErrorWrapper>
        )}
      </Field>
      <Field<string> name="externalInfoHyperlink.text">
        {field => (
          <RFFFieldErrorWrapper field={field}>
            {input => (
              <StyledInputContainer
                characterCount={{ value: field.input.value.length, max: 80 }}
                label={intl.formatMessage({ id: 'menu-item.external-item-link.text-label' })}
              >
                <Input
                  {...input}
                  maxLength={80}
                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                  disabled={!menuPermissions.contentEdit}
                />
              </StyledInputContainer>
            )}
          </RFFFieldErrorWrapper>
        )}
      </Field>
    </ColumnContainerWithGap>
  );

  if (shouldHideRow) {
    return null;
  }

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.external-item-link.form-row-label' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      shouldDisplayCreateTag={!values.externalInfoHyperlink}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
